// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { AutoDeliveryAndCarriersDataModel } from "@guestsupply/App/Components/Product/AutoDeliveryLink";
import {
    AutoDeliveryCollectionModel,
    AutoDeliveryItemModel,
    GetAutoDeliveryCollectionApiParameter,
    GetEstimateTotalsModel,
} from "@guestsupply/App/Services/AutoDelivery";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";

import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { DataViewState, setDataViewLoaded, setDataViewLoading } from "@insite/client-framework/Store/Data/DataState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { UpdateSearchFieldsType } from "@insite/client-framework/Types/UpdateSearchFieldsType";
import { Draft } from "immer";

interface ModalData {
    isOpen?: boolean;
    autoDeliveryAndCarriersData?: AutoDeliveryAndCarriersDataModel;
    estimateTotalsData?: GetEstimateTotalsModel;
    errorMessage?: string;
    isDataLoading?: boolean;
    productContext?: ProductContextModel;
}
export interface AutoDeliveryState extends DataViewState<AutoDeliveryItemModel> {
    page: number;
    pageSize?: number;
    autoDeliveryOrdersType?: boolean;
    parameter?: GetAutoDeliveryCollectionApiParameter;
    autoDeliveryDtos?: AutoDeliveryItemModel;
    modalData?: ModalData;
    id?: "";
}

const initialState: AutoDeliveryState = {
    page: 1,
    pageSize: 8,
    autoDeliveryOrdersType: false,
    parameter: {},
    isLoading: {},
    byId: {},
    dataViews: {},
    modalData: {},
    id: "",
};

const reducer = {
    "xAutoDelivery/BeginLoadOrders": (
        draft: Draft<AutoDeliveryState>,
        action: { parameter: GetAutoDeliveryCollectionApiParameter },
    ) => {
        setDataViewLoading(draft, action.parameter);
    },
    "xAutoDelivery/CompleteLoadOrders": (
        draft: Draft<AutoDeliveryState>,
        action: { parameter: GetAutoDeliveryCollectionApiParameter; collection: AutoDeliveryCollectionModel },
    ) => {
        setDataViewLoaded(draft, action.parameter, action.collection, collection => collection?.autoDeliveryDtos || []);
    },
    "xAutoDelivery/UpdateParameter": (
        draft: Draft<AutoDeliveryState>,
        action: { parameter: GetAutoDeliveryCollectionApiParameter & UpdateSearchFieldsType },
    ) => {
        const { type } = action.parameter;
        delete action.parameter.type;
        if (type === "Replace") {
            draft.parameter = action.parameter;
        } else if (type === "Initialize") {
            draft.parameter = { ...initialState.parameter, ...action.parameter };
        } else {
            draft.parameter = { ...draft.parameter, ...action.parameter };
            for (const key in draft.parameter) {
                const value = (<any>draft.parameter)[key];
                // remove empty parameters
                if (value === "" || value === undefined) {
                    delete (<any>draft.parameter)[key];
                }
            }
        }
    },
    "xAutoDelivery/SetAutoDeliveryOrdersType": (
        draft: Draft<AutoDeliveryState>,
        action: {
            autoDeliveryOrdersType?: boolean;
        },
    ) => {
        draft.autoDeliveryOrdersType = action.autoDeliveryOrdersType;
    },
    "xAutoDelivery/CompleteSetModalData": (
        draft: Draft<AutoDeliveryState>,
        action: {
            autoDeliveryAndCarriersData?: AutoDeliveryAndCarriersDataModel;
            estimateTotalsData?: GetEstimateTotalsModel;
            productContext?: ProductContextModel;
        },
    ) => {
        if (draft.modalData) {
            draft.modalData.autoDeliveryAndCarriersData = action.autoDeliveryAndCarriersData;
            draft.modalData.estimateTotalsData = action.estimateTotalsData;
            draft.modalData.productContext = action.productContext;
        }
    },
    "xAutoDelivery/CompleteSetestimateTotalsData": (
        draft: Draft<AutoDeliveryState>,
        action: {
            estimateTotalsData: GetEstimateTotalsModel;
        },
    ) => {
        if (draft.modalData) {
            draft.modalData.estimateTotalsData = action.estimateTotalsData;
        }
    },
    "xAutoDelivery/CompleteSetModalOpen": (draft: Draft<AutoDeliveryState>) => {
        if (draft.modalData) {
            draft.modalData.isOpen = true;
        }
    },
    "xAutoDelivery/CompleteSetModalClosed": (draft: Draft<AutoDeliveryState>) => {
        if (draft.modalData) {
            draft.modalData.isOpen = false;
            draft.modalData.autoDeliveryAndCarriersData = undefined;
            draft.modalData.estimateTotalsData = undefined;
            draft.modalData.errorMessage = undefined;
            draft.modalData.productContext = undefined;
        }
    },
    "xAutoDelivery/SetModalIsDataLoading": (
        draft: Draft<AutoDeliveryState>,
        action: {
            isDataLoading?: boolean;
        },
    ) => {
        if (draft.modalData) {
            draft.modalData.isDataLoading = action.isDataLoading;
        }
    },
    "xAutoDelivery/SetModalErrorMessage": (
        draft: Draft<AutoDeliveryState>,
        action: {
            errorMessage?: string;
        },
    ) => {
        if (draft.modalData) {
            draft.modalData.errorMessage = action.errorMessage;
        }
    },
    "xAutoDelivery/ChangeQuantityOrdered": (
        draft: Draft<AutoDeliveryState>,
        action: { qtyOrdered: number; productId: string },
    ) => {
        const productInfo = draft.modalData?.productContext?.productInfo;

        if (productInfo?.productId === action.productId) {
            productInfo.qtyOrdered = action.qtyOrdered;
        }
    },
};

const xAutoDelivery = createTypedReducerWithImmer(initialState, reducer);

(reducers as any).xAutoDelivery = xAutoDelivery;

export type xAutoDelivery = {
    xAutoDelivery: AutoDeliveryState;
} & ApplicationState;

type AutoDeliveryReducer = {
    [key: string]: typeof xAutoDelivery;
};

export type AutoDeliveryActions = Parameters<AutoDeliveryReducer[keyof AutoDeliveryReducer]>[1] | AnyAction;
